import React, { useMemo, useState, useEffect, useCallback } from 'react';
import NumberField from '../../ui/NumberField';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  Alert,
  AlertTitle,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
} from '@mui/material';
import { TimePicker, DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import useBooking from '../../../hooks/useBooking';
import useResource from '../../../hooks/useResource';
import { toast } from 'react-toastify';
import { Link as RouterLink } from 'react-router-dom';
import { BLOCKER_REASON_OPTIONS, BLOCKER_REASON_OTHER } from '../../../constants/blockerReasons';
import { useAuth } from '../../../context/authContext';

const GROUP_TYPE_LABELS = {
  kids: 'Kinder',
  adults: 'Erwachsene',
};

function computeSegments(startH, startM, endH, endM, slotGroupTypes, ltSlotCap) {
  if (!slotGroupTypes || typeof slotGroupTypes !== 'object') return null;

  const startMin = startH * 60 + startM;
  const endMin = endH * 60 + endM;
  if (endMin <= startMin) return null;

  const segments = [];
  let currentGt;
  let segStartMin = startMin;

  for (let min = startMin; min < endMin; min += 30) {
    const h = Math.floor(min / 60);
    const m = min % 60 === 0 ? '00' : '30';
    const key = `${h}:${m}`;
    const gt = slotGroupTypes[key] ?? null;

    if (currentGt === undefined) {
      currentGt = gt;
      segStartMin = min;
    } else if (gt !== currentGt) {
      segments.push({
        startHour: Math.floor(segStartMin / 60),
        startMinute: segStartMin % 60,
        endHour: Math.floor(min / 60),
        endMinute: min % 60,
        groupType: currentGt,
      });
      currentGt = gt;
      segStartMin = min;
    }
  }

  if (currentGt !== undefined) {
    segments.push({
      startHour: Math.floor(segStartMin / 60),
      startMinute: segStartMin % 60,
      endHour: endH,
      endMinute: endM,
      groupType: currentGt,
    });
  }

  if (segments.length === 0) return null;

  for (const seg of segments) {
    if (seg.groupType === 'kids') {
      seg.maxKids = ltSlotCap * 2;
      seg.maxAdults = 0;
    } else if (seg.groupType === 'adults') {
      seg.maxKids = 0;
      seg.maxAdults = ltSlotCap * 2;
    } else {
      seg.maxKids = ltSlotCap;
      seg.maxAdults = ltSlotCap;
    }
  }

  return segments;
}

function formatHM(h, m) {
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/**
 * Bookable capacity of one slot, derived from the entertainment. Mirrors the
 * backend's per-slot capacity (`getParticipantCapacity` in
 * internal-booking.internal.handlers.js) — a blocker only closes a slot when it
 * covers that full capacity:
 * - LaserZone runs two overlapping game groups per slot, so the slot holds
 *   twice the vest count (`ltSlotCapacity`). Age-split slots are served per
 *   group and keep the plain vest count as the per-group pool.
 * - PaintballZone runs four groups per slot.
 * - Lane-based entertainments (AxeZone, PixelZone) count capacity in lanes.
 * Only used as a fallback: the `capacity` prop carries the server-computed
 * capacity of the viewed day whenever the view has one.
 */
function getEntertainmentCapacity(entertainment) {
  if (!entertainment) return 1;

  // The bookings view hands over its option shape, where the entertainment name
  // is the option label.
  const entertainmentName = entertainment.entertainmentName || entertainment.label;

  if (entertainmentName === 'LaserZone') {
    const cap = Number(entertainment.ltSlotCapacity || 0);
    if (cap > 0) return entertainment.ltCapacityMode === 'ageSplit' ? cap : cap * 2;
  }

  if (entertainmentName === 'PaintballZone') {
    const cap = Number(entertainment.pbSlotCapacity || 0);
    if (cap > 0) return cap * 4;
  }

  const laneCount = Number(entertainment.laneCount || 0);
  if (entertainment.participantLaneDensity && laneCount > 0) return laneCount;

  return Number(entertainment.ltSlotCapacity || entertainment.pbSlotCapacity || 1) || 1;
}

const CreateBlockerModal = ({ show, onHide, branchId, entertainmentId, onSuccess, capacity, entertainment, directMode = false }) => {
  // The backend-computed capacity of the viewed day wins; deriving it from the
  // entertainment is only the fallback while that value is not available.
  const effectiveCapacity =
    Number(capacity) > 0 ? Number(capacity) : getEntertainmentCapacity(entertainment);

  const [startDate, setStartDate] = useState(dayjs());
  const [startTime, setStartTime] = useState(dayjs().hour(9).minute(0));
  const [endDate, setEndDate] = useState(dayjs());
  const [endTime, setEndTime] = useState(dayjs().hour(17).minute(0));
  const [maxCapacity, setMaxCapacity] = useState(effectiveCapacity);
  const [blockedSlots, setBlockedSlots] = useState(effectiveCapacity);
  const [reasonCategory, setReasonCategory] = useState('');
  const [reasonText, setReasonText] = useState('');
  const { createTimeslotBlocker, createBlockerRequest, previewTimeslotBlockerSnap, getTimeslotBlockerTimeOptions } =
    useBooking();
  const { getResourcesByEntertainmentId } = useResource();
  const { checkRight } = useAuth();
  const canReportIssue = checkRight('issue:create');
  const [resources, setResources] = useState([]);
  const [selectedResource, setSelectedResource] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snapPreview, setSnapPreview] = useState(null);
  const [snapPreviewError, setSnapPreviewError] = useState(null);
  const [isSnapPreviewLoading, setIsSnapPreviewLoading] = useState(false);
  const [timeOptions, setTimeOptions] = useState(null);
  const [timeOptionsError, setTimeOptionsError] = useState(null);
  const [isTimeOptionsLoading, setIsTimeOptionsLoading] = useState(false);
  const [segmentConfigs, setSegmentConfigs] = useState([]);

  const isAgeSplit = entertainment?.ltCapacityMode === 'ageSplit';
  const isSameDay = startDate && endDate && startDate.isSame(endDate, 'day');
  const slotGroupTypes = timeOptions?.slotGroupTypes;

  const singlePoolCapacity = effectiveCapacity;
  const ltSlotCapacity = Number(entertainment?.ltSlotCapacity || 0);

  const isTimeValid =
    startDate &&
    startTime &&
    endDate &&
    endTime &&
    (startDate.isBefore(endDate) || (startDate.isSame(endDate) && startTime.isBefore(endTime)));

  const isResourceSelectionRequired = resources.length > 0;

  const segments = useMemo(() => {
    if (!isAgeSplit || !isSameDay || !slotGroupTypes || !startTime || !endTime || !isTimeValid) return null;
    return computeSegments(
      startTime.hour(), startTime.minute(),
      endTime.hour(), endTime.minute(),
      slotGroupTypes,
      ltSlotCapacity
    );
  }, [isAgeSplit, isSameDay, slotGroupTypes, startTime, endTime, isTimeValid, ltSlotCapacity]);

  const hasSegments = Array.isArray(segments) && segments.length > 0;

  useEffect(() => {
    if (!hasSegments) {
      setSegmentConfigs([]);
      return;
    }
    setSegmentConfigs(
      segments.map(seg => ({
        kids: seg.maxKids,
        adults: seg.maxAdults,
      }))
    );
  }, [hasSegments, segments]);

  const updateSegmentConfig = useCallback((index, field, nextValue) => {
    const seg = segments?.[index];
    const max = field === 'kids' ? (seg?.maxKids ?? singlePoolCapacity) : (seg?.maxAdults ?? singlePoolCapacity);
    // NumberField already keeps the entry inside the limits; this stays as the last guard.
    const value = Math.min(Math.max(0, Number(nextValue) || 0), max);
    setSegmentConfigs(prev => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  }, [segments, singlePoolCapacity]);

  const handleStartDateChange = value => {
    setStartDate(value);
    if (value && endDate && value.isAfter(endDate)) {
      setEndDate(value);
    }
  };

  useEffect(() => {
    if (show && entertainmentId) {
      const fetchData = async () => {
        try {
          const fetched = await getResourcesByEntertainmentId(entertainmentId, true);
          const list = Array.isArray(fetched) ? fetched : fetched?.resources || [];
          setResources(Array.isArray(list) ? list : []);
        } catch (error) {
          console.error('Error fetching resources/entertainment:', error);
          toast.error('Fehler beim Laden der Ressourcen');
          setResources([]);
        }
      };
      fetchData();
    } else if (!entertainmentId) {
      setResources([]);
    }
  }, [show, entertainmentId]);

  useEffect(() => {
    setStartDate(dayjs());
    setStartTime(dayjs().hour(9).minute(0));
    setEndDate(dayjs());
    setEndTime(dayjs().hour(17).minute(0));
    setMaxCapacity(effectiveCapacity);
    setBlockedSlots(effectiveCapacity);
    setReasonCategory('');
    setReasonText('');
    setSelectedResource('');
    setIsSubmitting(false);
    setSnapPreview(null);
    setSnapPreviewError(null);
    setIsSnapPreviewLoading(false);
    setTimeOptions(null);
    setTimeOptionsError(null);
    setIsTimeOptionsLoading(false);
    setSegmentConfigs([]);
  }, [effectiveCapacity, branchId, entertainmentId]);

  useEffect(() => {
    let active = true;
    let timer;

    const shouldLoad =
      show &&
      entertainmentId &&
      startDate &&
      !(isResourceSelectionRequired && !selectedResource);

    if (!shouldLoad) {
      setTimeOptions(null);
      setTimeOptionsError(null);
      setIsTimeOptionsLoading(false);
      return () => {};
    }

    const run = async () => {
      try {
        setIsTimeOptionsLoading(true);
        setTimeOptionsError(null);

        const resourceIds =
          selectedResource === 'all'
            ? resources.map(r => r._id)
            : selectedResource
              ? [selectedResource]
              : [];

        const res = await getTimeslotBlockerTimeOptions({
          entertainmentId,
          date: startDate.format('YYYY-MM-DD'),
          resourceIds,
        });

        if (!active) return;
        setTimeOptions(res && typeof res === 'object' ? res : null);
        if (res && res.warning) {
          setTimeOptionsError(String(res.warning));
        }
      } catch (_e) {
        if (!active) return;
        setTimeOptions(null);
        setTimeOptionsError('Zeitoptionen konnten nicht geladen werden.');
      } finally {
        if (!active) return;
        setIsTimeOptionsLoading(false);
      }
    };

    timer = setTimeout(run, 200);
    return () => {
      active = false;
      if (timer) clearTimeout(timer);
    };
  }, [
    show,
    entertainmentId,
    startDate,
    isResourceSelectionRequired,
    selectedResource,
    resources,
  ]);

  useEffect(() => {
    let active = true;
    let timer;

    const shouldPreview =
      show &&
      entertainmentId &&
      startDate &&
      startTime &&
      endDate &&
      endTime &&
      isTimeValid &&
      !(isResourceSelectionRequired && !selectedResource);

    if (!shouldPreview) {
      setSnapPreview(null);
      setSnapPreviewError(null);
      setIsSnapPreviewLoading(false);
      return () => {};
    }

    timer = setTimeout(async () => {
      try {
        setIsSnapPreviewLoading(true);
        setSnapPreviewError(null);

        const startDateTime = startDate
          .hour(startTime.hour())
          .minute(startTime.minute())
          .second(0)
          .millisecond(0);

        const endDateTime = endDate
          .hour(endTime.hour())
          .minute(endTime.minute())
          .second(0)
          .millisecond(0);

        const resourceIds =
          selectedResource === 'all'
            ? resources.map(r => r._id)
            : selectedResource
              ? [selectedResource]
              : [];

        const preview = await previewTimeslotBlockerSnap({
          startTime: startDateTime.valueOf(),
          endTime: endDateTime.valueOf(),
          entertainmentId,
          resourceIds,
        });

        if (!active) return;
        setSnapPreview(Array.isArray(preview) ? preview : null);
      } catch (_error) {
        if (!active) return;
        setSnapPreview(null);
        setSnapPreviewError('Vorschau konnte nicht berechnet werden.');
      } finally {
        if (!active) return;
        setIsSnapPreviewLoading(false);
      }
    }, 250);

    return () => {
      active = false;
      if (timer) clearTimeout(timer);
    };
  }, [
    show,
    entertainmentId,
    startDate,
    startTime,
    endDate,
    endTime,
    isTimeValid,
    isResourceSelectionRequired,
    selectedResource,
    resources,
  ]);

  // A reason is mandatory; the free text is only required for "Anderer Grund".
  const isOtherReason = reasonCategory === BLOCKER_REASON_OTHER;
  const isReasonValid = Boolean(reasonCategory) && (!isOtherReason || reasonText.trim().length > 0);

  const handleSubmit = async () => {
    if (!startDate || !startTime || !endDate || !endTime) return;
    if (!isReasonValid) return;

    try {
      setIsSubmitting(true);

      // Users with the elevated blockDirect right create blockers directly;
      // everyone else submits a blocker request that must be approved.
      const submitBlocker = directMode ? createTimeslotBlocker : createBlockerRequest;

      const resourceIds =
        selectedResource === 'all'
          ? resources.map(r => r._id)
          : selectedResource
            ? [selectedResource]
            : [];

      const basePayload = {
        branchId,
        entertainmentId,
        reasonCategory,
        reasonText: reasonText.trim() || undefined,
        resourceIds,
      };

      if (hasSegments && segmentConfigs.length === segments.length) {
        const requests = [];
        for (let i = 0; i < segments.length; i++) {
          const seg = segments[i];
          const config = segmentConfigs[i] || {};

          const segStart = startDate
            .hour(seg.startHour).minute(seg.startMinute).second(0).millisecond(0);
          const segEnd = startDate
            .hour(seg.endHour).minute(seg.endMinute).second(0).millisecond(0);

          const segBase = {
            ...basePayload,
            startTime: segStart.valueOf(),
            endTime: segEnd.valueOf(),
          };

          if (seg.groupType !== 'adults' && config.kids > 0) {
            requests.push(submitBlocker({ ...segBase, blockedSlots: config.kids, groupType: 'kids' }));
          }
          if (seg.groupType !== 'kids' && config.adults > 0) {
            requests.push(submitBlocker({ ...segBase, blockedSlots: config.adults, groupType: 'adults' }));
          }
        }
        await Promise.all(requests);
      } else {
        const startDateTime = startDate
          .hour(startTime.hour()).minute(startTime.minute()).second(0).millisecond(0);
        const endDateTime = endDate
          .hour(endTime.hour()).minute(endTime.minute()).second(0).millisecond(0);

        await submitBlocker({
          ...basePayload,
          startTime: startDateTime.valueOf(),
          endTime: endDateTime.valueOf(),
          blockedSlots,
        });
      }

      toast.success(directMode ? 'Zeitslot Blocker erstellt' : 'Blocker-Anfrage eingereicht');
      onSuccess();
      onHide();
    } catch (error) {
      console.error('Error creating blocker:', error);
      toast.error(
        directMode
          ? 'Fehler beim Erstellen des Zeitslot Blockers'
          : 'Fehler beim Einreichen der Blocker-Anfrage'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDateTime = ms => dayjs(ms).format('DD.MM.YYYY HH:mm');
  const formatTime = ms => dayjs(ms).format('HH:mm');

  const previewLines = useMemo(() => {
    if (!Array.isArray(snapPreview) || snapPreview.length === 0) return [];
    const byId = new Map((resources || []).map(r => [String(r._id), r]));
    return snapPreview.map(p => {
      const rid = p?.resourceId ? String(p.resourceId) : null;
      const resourceName = rid && byId.has(rid) ? byId.get(rid).name : null;
      const inputSameDay =
        dayjs(p.inputStartTime).format('DD.MM.YYYY') === dayjs(p.inputEndTime).format('DD.MM.YYYY');
      const snappedSameDay =
        dayjs(p.snappedStartTime).format('DD.MM.YYYY') ===
        dayjs(p.snappedEndTime).format('DD.MM.YYYY');

      const selectedLabel = inputSameDay
        ? `${formatDateTime(p.inputStartTime)}–${formatTime(p.inputEndTime)}`
        : `${formatDateTime(p.inputStartTime)}–${formatDateTime(p.inputEndTime)}`;
      const snappedLabel = snappedSameDay
        ? `${formatDateTime(p.snappedStartTime)}–${formatTime(p.snappedEndTime)}`
        : `${formatDateTime(p.snappedStartTime)}–${formatDateTime(p.snappedEndTime)}`;

      return {
        key: rid || 'global',
        title: resourceName || (snapPreview.length > 1 ? 'Global' : null),
        selectedLabel,
        snappedLabel,
        changed:
          Number(p.inputStartTime) !== Number(p.snappedStartTime) ||
          Number(p.inputEndTime) !== Number(p.snappedEndTime),
        stepMinutes: p?.stepMinutes,
        gridOffsetMinutesStart: p?.gridOffsetMinutesStart,
        resourceOffsetMinutes: p?.resourceOffsetMinutes,
      };
    });
  }, [snapPreview, resources]);

  const stepMinutes =
    previewLines.length > 0 && Number.isFinite(Number(previewLines[0].stepMinutes))
      ? Number(previewLines[0].stepMinutes)
      : null;

  const dropdownTimes = useMemo(() => {
    const ms = Array.isArray(timeOptions?.times) ? timeOptions.times : [];
    const labels = ms
      .map(t => Number(t))
      .filter(n => Number.isFinite(n))
      .sort((a, b) => a - b)
      .map(n => ({
        value: dayjs(n).format('HH:mm'),
        label: dayjs(n).format('HH:mm'),
        hour: dayjs(n).hour(),
        minute: dayjs(n).minute(),
      }));
    const seen = new Set();
    return labels.filter(x => {
      if (seen.has(x.value)) return false;
      seen.add(x.value);
      return true;
    });
  }, [timeOptions]);

  const startTimeValue = startTime ? startTime.format('HH:mm') : '';
  const endTimeValue = endTime ? endTime.format('HH:mm') : '';
  const hasDropdownTimes = Array.isArray(dropdownTimes) && dropdownTimes.length > 0;
  const shouldDisableTimeInputs = isResourceSelectionRequired && !selectedResource;

  const isSegmentSubmitDisabled = hasSegments && segmentConfigs.every(
    c => (c.kids || 0) === 0 && (c.adults || 0) === 0
  );

  return (
    <Dialog open={show} onClose={onHide} maxWidth='md' fullWidth>
      <DialogTitle>{directMode ? 'Zeitslot Blocker erstellen' : 'Blocker-Anfrage erstellen'}</DialogTitle>
      <Box paddingX={2} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Alert severity='warning'>
          <AlertTitle>ACHTUNG!</AlertTitle>
          Die Kapazität wird nicht berücksichtigt und muss manuell geprüft werden!
        </Alert>
        <Alert severity='error'>
          <AlertTitle>Blocker wegen eines Defekts?</AlertTitle>
          Dieser Blocker ist nur für organisatorische Gründe gedacht (z. B. Sommerfest,
          Besprechung, kein Personal). Ist etwas defekt oder nicht bespielbar, muss stattdessen
          ein Issue gemeldet werden — die Sperrung wird dann automatisch über das Ausfall-Board
          erzeugt und wieder freigegeben.
          {canReportIssue && (
            <Box sx={{ mt: 1 }}>
              <Button
                component={RouterLink}
                to='/issues/report'
                size='small'
                variant='outlined'
                color='error'
                onClick={onHide}
              >
                Issue melden
              </Button>
            </Box>
          )}
        </Alert>
      </Box>
      <DialogContent>
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Alert severity='info'>
            <AlertTitle>Hinweis</AlertTitle>
            Beim Speichern werden Zeiten automatisch auf das Buchungsraster gerundet (Start wird
            abgerundet, Ende aufgerundet).
            {Number.isFinite(Number(timeOptions?.stepMinutes)) && Number(timeOptions.stepMinutes) > 0
              ? ` Raster: alle ${Number(timeOptions.stepMinutes)} Minuten.`
              : stepMinutes
                ? ` Raster: alle ${stepMinutes} Minuten.`
                : ''}
          </Alert>

          {(isTimeOptionsLoading || timeOptionsError) && (
            <Typography variant='body2' color={timeOptionsError ? 'error' : 'text.secondary'}>
              {isTimeOptionsLoading ? 'Lade Zeitoptionen…' : timeOptionsError}
            </Typography>
          )}

          {(isSnapPreviewLoading || snapPreviewError || previewLines.length > 0) && (
            <Box>
              <Typography sx={{ fontWeight: 'bold', color: 'text.secondary', mb: 0.5 }}>
                Vorschau (wird gespeichert als)
              </Typography>
              {isSnapPreviewLoading && (
                <Typography variant='body2' color='text.secondary'>
                  Berechne Vorschau…
                </Typography>
              )}
              {!isSnapPreviewLoading && snapPreviewError && (
                <Typography variant='body2' color='error'>
                  {snapPreviewError}
                </Typography>
              )}
              {!isSnapPreviewLoading &&
                !snapPreviewError &&
                previewLines.map(line => (
                  <Box key={line.key} sx={{ mb: 1 }}>
                    {line.title && (
                      <Typography variant='body2' sx={{ fontWeight: 600 }}>
                        {line.title}
                      </Typography>
                    )}
                    <Typography variant='body2' color='text.secondary'>
                      Auswahl: {line.selectedLabel}
                    </Typography>
                    <Typography variant='body2'>
                      {line.changed ? `Gespeichert als: ${line.snappedLabel}` : line.snappedLabel}
                    </Typography>
                  </Box>
                ))}
            </Box>
          )}

          {resources && resources.length > 0 && (
            <FormControl fullWidth>
              <InputLabel>Raum</InputLabel>
              <Select
                value={selectedResource}
                onChange={e => setSelectedResource(e.target.value)}
                label='Raum'
              >
                <MenuItem value='all'>Alle Räume</MenuItem>
                {resources.map(resource => (
                  <MenuItem key={resource._id} value={resource._id}>
                    {resource.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Start Date and Time */}
          <Box>
            <Box sx={{ mb: 1, fontWeight: 'bold', color: 'text.secondary' }}>Start</Box>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label='Startdatum'
                  value={startDate}
                  onChange={handleStartDateChange}
                  format='DD.MM.YYYY'
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      margin: 'normal',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                {hasDropdownTimes ? (
                  <FormControl fullWidth margin='normal'>
                    <InputLabel id='start-time-label'>Startzeit</InputLabel>
                    <Select
                      labelId='start-time-label'
                      label='Startzeit'
                      value={startTimeValue}
                      onChange={e => {
                        const v = String(e.target.value || '');
                        const opt = dropdownTimes.find(o => o.value === v);
                        if (!opt) return;
                        setStartTime(dayjs().hour(opt.hour).minute(opt.minute));
                      }}
                    >
                      {dropdownTimes.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : shouldDisableTimeInputs || isTimeOptionsLoading ? (
                  <FormControl fullWidth margin='normal' disabled>
                    <InputLabel id='start-time-label'>Startzeit</InputLabel>
                    <Select labelId='start-time-label' label='Startzeit' value=''>
                      <MenuItem value=''>
                        {isTimeOptionsLoading
                          ? 'Lade Zeitoptionen…'
                          : 'Bitte zuerst einen Raum auswählen'}
                      </MenuItem>
                    </Select>
                  </FormControl>
                ) : (
                  <TimePicker
                    label='Startzeit'
                    value={startTime}
                    onChange={setStartTime}
                    format='HH:mm'
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        margin: 'normal',
                      },
                    }}
                  />
                )}
              </Grid>
            </Grid>
          </Box>

          {/* End Date and Time */}
          <Box>
            <Box sx={{ mb: 1, fontWeight: 'bold', color: 'text.secondary' }}>Ende</Box>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label='Enddatum'
                  value={endDate}
                  onChange={setEndDate}
                  format='DD.MM.YYYY'
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      margin: 'normal',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                {hasDropdownTimes ? (
                  <FormControl fullWidth margin='normal'>
                    <InputLabel id='end-time-label'>Endzeit</InputLabel>
                    <Select
                      labelId='end-time-label'
                      label='Endzeit'
                      value={endTimeValue}
                      onChange={e => {
                        const v = String(e.target.value || '');
                        const opt = dropdownTimes.find(o => o.value === v);
                        if (!opt) return;
                        setEndTime(dayjs().hour(opt.hour).minute(opt.minute));
                      }}
                      error={startDate && startTime && endDate && endTime && !isTimeValid}
                    >
                      {dropdownTimes.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {!isTimeValid && (
                      <Typography variant='caption' color='error' sx={{ mt: 0.5 }}>
                        Endzeit muss nach Startzeit liegen (bei gleichem Datum)
                      </Typography>
                    )}
                  </FormControl>
                ) : shouldDisableTimeInputs || isTimeOptionsLoading ? (
                  <FormControl fullWidth margin='normal' disabled>
                    <InputLabel id='end-time-label'>Endzeit</InputLabel>
                    <Select labelId='end-time-label' label='Endzeit' value=''>
                      <MenuItem value=''>
                        {isTimeOptionsLoading ? 'Lade Zeitoptionen…' : 'Bitte zuerst einen Raum auswählen'}
                      </MenuItem>
                    </Select>
                  </FormControl>
                ) : (
                  <TimePicker
                    label='Endzeit'
                    value={endTime}
                    onChange={setEndTime}
                    format='HH:mm'
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        margin: 'normal',
                        error: startDate && startTime && endDate && endTime && !isTimeValid,
                        helperText:
                          startDate && startTime && endDate && endTime && !isTimeValid
                            ? 'Endzeit muss nach Startzeit liegen (bei gleichem Datum)'
                            : '',
                      },
                    }}
                  />
                )}
              </Grid>
            </Grid>
          </Box>

          {hasSegments ? (
            <Box>
              <Divider sx={{ my: 1 }} />
              <Typography variant='subtitle2' sx={{ mb: 1, fontWeight: 'bold' }}>
                Kapazität pro Zeitbereich
              </Typography>
              {segments.map((seg, i) => {
                const config = segmentConfigs[i] || { kids: 0, adults: 0 };
                const label = seg.groupType
                  ? GROUP_TYPE_LABELS[seg.groupType]
                  : 'Gemischt';
                const showKids = seg.groupType !== 'adults';
                const showAdults = seg.groupType !== 'kids';

                return (
                  <Box
                    key={`${seg.startHour}-${seg.startMinute}-${seg.endHour}-${seg.endMinute}`}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 2,
                      mb: 1.5,
                    }}
                  >
                    <Typography variant='body2' sx={{ fontWeight: 600, mb: 1 }}>
                      {formatHM(seg.startHour, seg.startMinute)} – {formatHM(seg.endHour, seg.endMinute)}
                      {' '}
                      <Typography
                        component='span'
                        variant='body2'
                        sx={{
                          color: seg.groupType === 'kids'
                            ? 'info.main'
                            : seg.groupType === 'adults'
                              ? 'warning.main'
                              : 'text.secondary',
                          fontWeight: 600,
                        }}
                      >
                        ({label})
                      </Typography>
                    </Typography>
                    <Grid container spacing={2}>
                      {showKids && (
                        <Grid item xs={showAdults ? 6 : 12}>
                          <NumberField
                            label='Kinder-Slots'
                            value={config.kids}
                            min={0}
                            max={seg.maxKids}
                            onValueChange={v => updateSegmentConfig(i, 'kids', v)}
                            helperText={`Max. ${seg.maxKids}`}
                            fullWidth
                            size='small'
                          />
                        </Grid>
                      )}
                      {showAdults && (
                        <Grid item xs={showKids ? 6 : 12}>
                          <NumberField
                            label='Erwachsene-Slots'
                            value={config.adults}
                            min={0}
                            max={seg.maxAdults}
                            onValueChange={v => updateSegmentConfig(i, 'adults', v)}
                            helperText={`Max. ${seg.maxAdults}`}
                            fullWidth
                            size='small'
                          />
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                );
              })}
            </Box>
          ) : (
            <NumberField
              label='Blockierte Slots'
              value={blockedSlots}
              min={1}
              max={maxCapacity}
              onValueChange={setBlockedSlots}
              helperText={
                maxCapacity
                  ? `Maximal ${maxCapacity} ${maxCapacity === 1 ? 'Slot' : 'Slots'} verfügbar`
                  : ''
              }
              fullWidth
              margin='normal'
            />
          )}
          <FormControl fullWidth margin='normal' required error={!reasonCategory}>
            <InputLabel id='blocker-reason-label'>Grund</InputLabel>
            <Select
              labelId='blocker-reason-label'
              label='Grund'
              value={reasonCategory}
              onChange={e => setReasonCategory(e.target.value)}
            >
              {BLOCKER_REASON_OPTIONS.map(option => (
                <MenuItem key={option.key} value={option.key}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            <Typography variant='caption' color={reasonCategory ? 'text.secondary' : 'error'} sx={{ mt: 0.5 }}>
              {reasonCategory
                ? 'Der Grund ist in der Blocker-Übersicht und in der Benachrichtigung sichtbar.'
                : 'Bitte einen Grund auswählen — ohne Grund kann kein Blocker angelegt werden.'}
            </Typography>
          </FormControl>

          <TextField
            label={isOtherReason ? 'Grund (Freitext)' : 'Ergänzung (optional)'}
            value={reasonText}
            onChange={e => setReasonText(e.target.value)}
            placeholder={
              isOtherReason
                ? 'Bitte den Grund für die Blockierung beschreiben'
                : 'Optionale Ergänzung zum ausgewählten Grund'
            }
            required={isOtherReason}
            error={isOtherReason && reasonText.trim().length === 0}
            helperText={
              isOtherReason && reasonText.trim().length === 0
                ? 'Bei "Anderer Grund" ist eine Beschreibung erforderlich.'
                : ''
            }
            fullWidth
            margin='normal'
            multiline
            rows={2}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onHide} color='inherit'>
          Abbrechen
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={
            !startDate ||
            !startTime ||
            !endDate ||
            !endTime ||
            isSubmitting ||
            !isTimeValid ||
            !isReasonValid ||
            (isResourceSelectionRequired && !selectedResource) ||
            isSegmentSubmitDisabled
          }
          variant='contained'
          color='primary'
        >
          {isSubmitting
            ? directMode
              ? 'Wird erstellt...'
              : 'Wird eingereicht...'
            : directMode
              ? 'Erstellen'
              : 'Anfrage einreichen'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateBlockerModal;
